import {
  PaymentMethod,
  PaymentMethodWithStripe as CommonPaymentMethodWithStripe,
  Locales,
  OrganizationCountry,
  PaymentMethod as GqlPaymentMethod,
  getStripeFrontendApiKeyFromCountry,
  StripePaymentMethod,
} from "@simplyk/common";
import { loadStripe, StripeConstructorOptions, StripeElementsOptionsMode } from "@stripe/stripe-js";

export const STRIPE_PUBLIC_KEYS = {
  NEXT_PUBLIC_STRIPE_PUBLIC_KEY_CA: process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY_CA,
  NEXT_PUBLIC_STRIPE_PUBLIC_KEY_UK: process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY_UK,
  NEXT_PUBLIC_STRIPE_PUBLIC_KEY_USA: process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY_USA,
  NEXT_PUBLIC_STRIPE_PUBLIC_KEY_AU: process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY_AU,
  NEXT_PUBLIC_STRIPE_PUBLIC_KEY_EU: process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY_EU,
} as const;

export const getStripeInstance = async (country: OrganizationCountry, locale: Locales) => {
  const stripePublicApiKey = getStripeFrontendApiKeyFromCountry(country, STRIPE_PUBLIC_KEYS);
  return loadStripe(stripePublicApiKey || "", {
    locale: locale.toLowerCase() as StripeConstructorOptions["locale"],
  });
};

export const serializeLocale = (locale: Locales) => {
  return String(locale).toLowerCase() as StripeConstructorOptions["locale"];
};

export const convertToGqlPaymentMethod = (stripePaymentMethod: CommonPaymentMethodWithStripe) => {
  if (PaymentMethod.ApplePayOrGooglePay === stripePaymentMethod) {
    return GqlPaymentMethod.ApplePayOrGooglePay;
  }
  if (PaymentMethod.Card === stripePaymentMethod) {
    return GqlPaymentMethod.Card;
  }
  if (PaymentMethod.Ach === stripePaymentMethod) {
    return GqlPaymentMethod.Ach;
  }
  if (PaymentMethod.Pad === stripePaymentMethod) {
    return GqlPaymentMethod.Pad;
  }
  if (PaymentMethod.Sepa === stripePaymentMethod) {
    return GqlPaymentMethod.Sepa;
  }
  if (PaymentMethod.CashApp === stripePaymentMethod) {
    return GqlPaymentMethod.CashApp;
  }

  throw new Error(`Unknown payment method: ${stripePaymentMethod}`);
};

export type PaymentMode = NonNullable<StripeElementsOptionsMode["mode"]>;

export type PaymentMethodWithStripe =
  | PaymentMethod.Card
  | PaymentMethod.Ach
  | PaymentMethod.Sepa
  | PaymentMethod.CashApp;

export const STRIPE_PAYMENT_METHOD_FROM_PAYMENT_METHOD: Record<PaymentMethodWithStripe, StripePaymentMethod> = {
  [PaymentMethod.Card]: "card",
  [PaymentMethod.Ach]: "us_bank_account",
  [PaymentMethod.Sepa]: "sepa_debit",
  [PaymentMethod.CashApp]: "cashapp",
};

export const isPaymentMethodWithStripe = (method: PaymentMethod): method is PaymentMethodWithStripe =>
  method === PaymentMethod.Card ||
  method === PaymentMethod.Ach ||
  method === PaymentMethod.Sepa ||
  method === PaymentMethod.CashApp;

export const getStripeElementsOnBehalfOf = (
  paymentMethod: PaymentMethod | undefined,
  stripeCustomAccountId: string | null | undefined
): string | null => {
  return paymentMethod === PaymentMethod.Sepa ? stripeCustomAccountId || null : null;
};

export const getStripeElementsSetupFutureUsage = (
  paymentMethod: PaymentMethod | undefined,
  paymentMode: PaymentMode
): "off_session" | null =>
  paymentMethod === PaymentMethod.Ach ||
  paymentMethod === PaymentMethod.Pad ||
  (paymentMethod === PaymentMethod.Sepa && paymentMode === "subscription")
    ? "off_session"
    : null;

export const getDeclineCodeFromServerError = (error: unknown): string | undefined => {
  const declineCode = (error as { declineCode?: unknown } | null | undefined)?.declineCode;
  return typeof declineCode === "string" ? declineCode : undefined;
};
